import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CalendarDays,
  Clock,
  Plus,
  Filter,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Edit,
  Trash2,
  FileText,
  Users,
  TrendingUp,
  Timer,
  CalendarX,
  BookOpen,
  Send,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { leaveAPI, authAPI, employeesAPI } from "@/lib/api"; // Import real API
import { exportLeaveReport } from "@/services/pdfExport.service"; // Import PDF export

// Import components (will create these)
import LeaveRequestForm from "./components/LeaveRequestForm";
import LeaveCalendar from "./components/LeaveCalendar";
import LeaveBalance from "./components/LeaveBalance";
import LeaveApproval from "./components/LeaveApproval";
import EmployeeLeaveCredits from "./components/EmployeeLeaveCredits";
import EmployeeLeave from "./EmployeeLeave";

export default function LeaveManagementPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  // State management
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requests"); // Start with "Mes demandes" for all roles
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState("all"); // Add employee filter
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    to: new Date(new Date().setMonth(new Date().getMonth() + 2)),
  });

  // Data states
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({});
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [employees, setEmployees] = useState([]); // Add employees state
  const [currentUser, setCurrentUser] = useState(null); // Add current user state
  const [stats, setStats] = useState({
    pendingRequests: 0,
    approvedRequests: 0,
    totalDaysUsed: 0,
    remainingDays: 0,
    upcomingLeaves: 0,
    teamOnLeave: 0,
  });
  const [requestCount, setRequestCount] = useState(0); // Total count for "Liste des demandes"

  // Modals and forms
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestDetail, setShowRequestDetail] = useState(false); // Add detail modal state

  // Load leave data from API
  const loadLeaveData = async () => {
    try {
      setLoading(true);

      // Fetch current user info
      const currentUserResponse = await authAPI.getCurrentUser();
      const user = currentUserResponse.data.data.user;
      const userId = user.id;
      setCurrentUser(user); // Store current user

      // Parallel API calls for better performance
      const apiCalls = [
        // Get leave types from database
        leaveAPI.getTypes(),

        // Get leave requests (filtered by user role automatically in backend)
        leaveAPI.getRequests({
          page: 1,
          limit: 1000, // Get all as requested (paginated in UI if needed)
        }),

        // Get current user's leave balance
        leaveAPI
          .getBalance(userId, { year: new Date().getFullYear() })
          .catch(() => ({ data: { data: { balances: [] } } })), // Fallback if balance API fails
      ];

      // Add employees call for managers/HR
      if (['manager', 'rh', 'admin'].includes(user.role)) {
        apiCalls.push(
          employeesAPI.getAll({ limit: 1000 }).catch(() => ({ data: { data: { employees: [] } } }))
        );
      }

      // Add request count call
      apiCalls.push(leaveAPI.getRequestCount().catch(() => ({ data: { count: 0 } })));

      const responses = await Promise.all(apiCalls);
      const [leaveTypesResponse, leaveRequestsResponse, leaveBalanceResponse] = responses;

      let nextResponseIndex = 3;
      const employeesResponse = ['manager', 'rh', 'admin'].includes(user.role)
        ? responses[nextResponseIndex++]
        : { data: { data: { employees: [] } } };

      const countResponse = responses[nextResponseIndex++];
      setRequestCount(countResponse?.data?.count || 0);

      // Process leave types
      const leaveTypes = leaveTypesResponse.data.data.leaveTypes || [];
      setLeaveTypes(leaveTypes);

      // Process leave requests
      const leaveRequests = leaveRequestsResponse.data.data.requests || [];

      // Ensure data structure consistency
      const normalizedRequests = leaveRequests.map((request) => ({
        ...request,
        // Ensure type_conge_id exists for filtering (use type_conge.id as fallback)
        type_conge_id: request.type_conge_id || request.type_conge?.id,
        // Ensure type_conge has the expected structure
        type_conge: request.type_conge || { id: null, nom: "Unknown" },
        // Ensure employe has the expected structure
        employe: request.employe || {
          id: null,
          nom: "Unknown",
          prenom: "Unknown",
        },
      }));

      setLeaveRequests(normalizedRequests);

      // Process employees if loaded
      const employees = employeesResponse.data.data.employees || [];
      setEmployees(employees);

      // Process leave balance
      const leaveBalance = leaveBalanceResponse.data.data.balances || [];

      // Convert balance array to object for easier access
      const balanceObject = {};
      leaveBalance.forEach((balance) => {
        // Fix: Use 'leaveType' instead of 'typeConge' to match backend API response
        const key =
          balance.leaveType?.nom?.toLowerCase().replace(/\s+/g, "_") ||
          "unknown";

        balanceObject[key] = {
          total: balance.allocated, // Fix: Use 'allocated' instead of 'totalAllowed'
          utilise: balance.used,
          restant: balance.remaining,
          pending: balance.pending || 0,
        };
      });
      console.log("balanceObject");
      console.log(balanceObject);

      setLeaveBalance(balanceObject);

      // Calculate personal stats from balance data (for all users)
      // This ensures all users see their personal stats regardless of role
      const personalStats = {
        // Use balance data directly for personal metrics
        totalDaysUsed: Object.values(balanceObject).reduce(
          (sum, balance) => sum + (balance.utilise || 0),
          0
        ),
        remainingDays: Object.values(balanceObject)
          .filter((item) => item.total)
          .reduce((sum, item) => sum + item.restant, 0),
        // For pending/approved counts, filter personal requests only
        // Handle both employe_id and employe.id patterns
        pendingRequests: leaveRequests.filter(
          (req) =>
            req.statut === "en_attente" &&
            (req.employe_id === user.id || req.employe?.id === user.id)
        ).length,
        approvedRequests: leaveRequests.filter(
          (req) =>
            req.statut === "approuve" &&
            (req.employe_id === user.id || req.employe?.id === user.id)
        ).length,
        upcomingLeaves: leaveRequests.filter(
          (req) =>
            req.statut === "approuve" &&
            (req.employe_id === user.id || req.employe?.id === user.id) &&
            new Date(req.date_debut) > new Date()
        ).length,
        teamOnLeave: leaveRequests.filter(
          (req) =>
            req.statut === "approuve" &&
            (req.employe_id === user.id || req.employe?.id === user.id) &&
            new Date(req.date_debut) <= new Date() &&
            new Date(req.date_fin) >= new Date()
        ).length,
      };

      // For admin/manager/RH, also calculate team stats
      let teamStats = {};
      if (["admin", "rh", "manager"].includes(user.role)) {
        teamStats = {
          totalTeamRequests: leaveRequests.length,
          pendingTeamRequests: leaveRequests.filter(
            (req) => req.statut === "en_attente"
          ).length,
          approvedTeamRequests: leaveRequests.filter(
            (req) => req.statut === "approuve"
          ).length,
          totalTeamDaysUsed: leaveRequests
            .filter((req) => req.statut === "approuve")
            .reduce((sum, req) => sum + (req.nombre_jours || 0), 0),
          upcomingTeamLeaves: leaveRequests.filter(
            (req) =>
              req.statut === "approuve" && new Date(req.date_debut) > new Date()
          ).length,
          currentTeamOnLeave: leaveRequests.filter(
            (req) =>
              req.statut === "approuve" &&
              new Date(req.date_debut) <= new Date() &&
              new Date(req.date_fin) >= new Date()
          ).length,
        };
      }

      // Combine personal and team stats
      setStats({
        ...personalStats,
        ...teamStats,
      });
    } catch (error) {
      console.error("Error loading leave data:", error);

      // If API calls fail, use fallback mock data with comment
      console.warn(
        "🔄 Using mock data - API calls failed. Make sure backend is running and these endpoints exist:"
      );
      console.warn("📝 Required API endpoints:");
      console.warn("   - GET /api/v1/leave/types (for leave types)");
      console.warn("   - GET /api/v1/leave/requests (for leave requests)");
      console.warn(
        "   - GET /api/v1/leave/balance/:userId (for leave balance)"
      );
      console.warn("   - GET /api/v1/auth/me (for current user)");

      // Fallback to mock data
      const mockLeaveTypes = [
        {
          id: "1",
          nom: "Congé Annuel",
          jours_autorises_par_an: 18,
          couleur: "#10B981",
          actif: true,
        },
        {
          id: "2",
          nom: "Congé Exceptionnel",
          jours_autorises_par_an: null,
          couleur: "#2196F3",
          actif: true,
        },
        {
          id: "3",
          nom: "Congé Maladie",
          jours_autorises_par_an: null,
          couleur: "#EF4444",
          actif: true,
        },
      ];

      const mockLeaveRequests = [
        {
          id: "1",
          employe_id: "emp1", // Add employe_id for consistent filtering
          type_conge_id: "1", // Add type_conge_id for consistent filtering
          employe: {
            id: "emp1",
            nom: "Dupont",
            prenom: "Jean",
            departement: { nom: "IT" },
          },
          type_conge: mockLeaveTypes[0],
          date_debut: "2025-08-15",
          date_fin: "2025-08-25",
          nombre_jours: 8,
          motif: "Vacances d'été",
          statut: "en_attente",
          date_demande: "2025-08-01",
          urgence: false,
        },
        {
          id: "2",
          employe_id: "emp2", // Add employe_id for consistent filtering
          type_conge_id: "2", // Add type_conge_id for consistent filtering
          employe: {
            id: "emp2",
            nom: "Martin",
            prenom: "Sophie",
            departement: { nom: "RH" },
          },
          type_conge: mockLeaveTypes[1],
          date_debut: "2025-08-12",
          date_fin: "2025-08-14",
          nombre_jours: 3,
          motif: "Maladie",
          statut: "approuve",
          date_demande: "2025-08-10",
          urgence: true,
        },
      ];

      const mockStats = {
        pendingRequests: 1,
        approvedRequests: 1,
        totalDaysUsed: 3,
        remainingDays: 22,
        upcomingLeaves: 1,
        teamOnLeave: 0,
      };

      const mockLeaveBalance = {
        conge_annuel: { total: 18, utilise: 3, restant: 15, pending: 8 },
        conge_exceptionnel: {
          total: null,
          utilise: 0,
          restant: null,
          pending: 0,
        },
        conge_maladie: { total: null, utilise: 0, restant: null, pending: 0 },
      };

      // Ensure mock data has consistent structure
      const normalizedMockRequests = mockLeaveRequests.map((request) => ({
        ...request,
        type_conge_id: request.type_conge_id, // Preserve type_conge_id for filtering
        type_conge: {
          id: request.type_conge.id,
          nom: request.type_conge.nom,
          couleur: request.type_conge.couleur,
        },
        employe: {
          id: request.employe.id,
          nom: request.employe.nom,
          prenom: request.employe.prenom,
          departement: request.employe.departement,
        },
      }));

      setLeaveTypes(mockLeaveTypes);
      setLeaveRequests(normalizedMockRequests);
      setStats(mockStats);
      setLeaveBalance(mockLeaveBalance);

      // Set mock current user based on role
      const mockCurrentUser = {
        id: "emp1", // Same as first employee for demo
        nom: "Dupont",
        prenom: "Jean",
        email: "jean.dupont@company.com",
        role: "employe", // Change this to test different roles: "employe", "admin", "rh", "manager"
      };
      setCurrentUser(mockCurrentUser);

      toast({
        title: "Mode démo",
        description:
          "Utilisation des données de démonstration. Vérifiez la connexion à l'API.",
        variant: "default",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeaveData();
  }, []);

  // Filter leave requests for different views
  const filteredRequests = useMemo(() => {
    return leaveRequests.filter((request) => {
      // Defensive programming - ensure required fields exist
      if (!request.employe || !request.type_conge) {
        return false;
      }

      const matchesSearch =
        request.employe.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.employe.prenom
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        request.motif?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        selectedStatus === "all" || request.statut === selectedStatus;

      // Fix type filtering - use type_conge_id instead of type_conge.id
      const matchesType =
        selectedType === "all" ||
        request.type_conge_id === selectedType ||
        request.type_conge_id === parseInt(selectedType) ||
        request.type_conge_id === selectedType.toString();

      const matchesEmployee =
        selectedEmployee === "all" || request.employe_id === selectedEmployee;

      return matchesSearch && matchesStatus && matchesType && matchesEmployee;
    });
  }, [leaveRequests, searchTerm, selectedStatus, selectedType, selectedEmployee]);

  // Filter personal requests (only current user's requests)
  const personalRequests = useMemo(() => {
    if (!currentUser) {
      return [];
    }

    return leaveRequests.filter((request) => {
      // Defensive programming - ensure required fields exist
      if (!request.employe || !request.type_conge) {
        return false;
      }

      // Handle both employe_id and employe.id patterns
      const isUserRequest =
        request.employe_id === currentUser.id ||
        request.employe?.id === currentUser.id;
      const matchesStatus =
        selectedStatus === "all" || request.statut === selectedStatus;

      // Fix type filtering - use type_conge_id instead of type_conge.id
      const matchesType =
        selectedType === "all" ||
        request.type_conge_id === selectedType ||
        request.type_conge_id === parseInt(selectedType) ||
        request.type_conge_id === selectedType.toString();

      const matchesEmployee =
        selectedEmployee === "all" || request.employe_id === selectedEmployee;

      return isUserRequest && matchesStatus && matchesType && matchesEmployee;
    });
  }, [leaveRequests, currentUser, selectedStatus, selectedType, selectedEmployee]);

  // Handle leave request actions with real API calls
  const handleApproveRequest = async (requestId, decision, comment = "") => {
    try {
      // Call real API to approve/reject request
      const response = await leaveAPI.approveRequest(requestId, {
        statut: decision,
        commentaire: comment,
      });

      toast({
        title: "Succès",
        description:
          response.data.message ||
          `Demande ${decision === "approuve" ? "approuvée" : "refusée"
          } avec succès`,
      });

      // Reload data to get updated state
      await loadLeaveData();

      setShowApprovalModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error("Error approving/rejecting leave request:", error);

      // Log what API should be called
      console.warn("🔄 API call failed - trying to call:");
      console.warn(`   PUT /api/v1/leave/requests/${requestId}/approve`);
      console.warn("   With body:", { statut: decision, commentaire: comment });
      console.warn("📝 Make sure this endpoint exists in your backend");

      // Fallback: Update local state (mock behavior)
      setLeaveRequests((prev) =>
        prev.map((req) =>
          req.id === requestId
            ? {
              ...req,
              statut: decision,
              date_approbation: new Date().toISOString(),
              commentaire_approbation: comment,
            }
            : req
        )
      );

      toast({
        title:
          decision === "approuve"
            ? "Approuvé (mode démo)"
            : "Refusé (mode démo)",
        description:
          "Action simulée localement. Vérifiez la connexion à l'API.",
        variant: "default",
      });

      setShowApprovalModal(false);
      setSelectedRequest(null);
    }
  };

  // Handle creating new leave request
  const handleCreateLeaveRequest = async (
    requestData,
    editRequestId = null
  ) => {
    try {
      let response;

      if (editRequestId) {
        // Update existing request
        response = await leaveAPI.updateRequest(editRequestId, requestData);
      } else {
        // Create new request
        response = await leaveAPI.createRequest(requestData);
      }

      toast({
        title: "Succès",
        description:
          response.data.message ||
          (editRequestId
            ? "Demande de congé mise à jour avec succès"
            : "Demande de congé créée avec succès"),
      });

      // Reload data to show updated request
      await loadLeaveData();

      setShowRequestForm(false);
      setSelectedRequest(null); // Clear selection after successful operation
    } catch (error) {
      console.error("Error creating/updating leave request:", error);

      // Log what API should be called
      if (editRequestId) {
        console.warn("🔄 API call failed - trying to call:");
        console.warn(`   PUT /api/v1/leave/requests/${editRequestId}`);
      } else {
        console.warn("🔄 API call failed - trying to call:");
        console.warn("   POST /api/v1/leave/requests");
      }
      console.warn("   With body:", requestData);
      console.warn("📝 Make sure this endpoint exists in your backend");

      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          (editRequestId
            ? "Erreur lors de la mise à jour de la demande"
            : "Erreur lors de la création de la demande"),
        variant: "destructive",
      });
    }
  };

  // Handle export functionality
  const handleExport = async () => {
    try {
      // Show loading state
      toast({
        title: "Génération du rapport...",
        description: "Récupération des données depuis le serveur...",
      });

      // Try to get comprehensive data from backend first
      let exportData;
      try {
        const response = await leaveAPI.exportData({
          format: "json",
          start_date: dateRange.from?.toISOString().split("T")[0],
          end_date: dateRange.to?.toISOString().split("T")[0],
        });

        exportData = response.data.data;
        console.log("✅ Using backend export data:", exportData);

        toast({
          title: "Génération du PDF...",
          description: "Création du document PDF...",
        });

        // Use backend data for PDF generation
        await exportLeaveReport({
          leaveRequests: exportData.leaveRequests || filteredRequests,
          leaveTypes: exportData.leaveTypes || leaveTypes,
          stats: exportData.stats || stats,
          user: exportData.exportInfo?.generatedBy || currentUser,
          dateRange: {
            from: dateRange.from,
            to: dateRange.to,
          },
        });
      } catch (backendError) {
        console.warn(
          "🔄 Backend export failed, using frontend data:",
          backendError
        );

        // Fallback to frontend data
        toast({
          title: "Mode hors ligne",
          description: "Génération du rapport avec les données locales...",
        });

        await exportLeaveReport({
          leaveRequests: filteredRequests,
          leaveTypes,
          stats,
          user: currentUser,
          dateRange: {
            from: dateRange.from,
            to: dateRange.to,
          },
        });

        console.warn("📝 To enable backend export, make sure:");
        console.warn("   - Backend server is running");
        console.warn("   - User has manager/rh/admin role");
        console.warn("   - GET /api/v1/leave/export endpoint is accessible");
      }

      toast({
        title: "Export réussi",
        description: "Le rapport de congés a été téléchargé",
      });
    } catch (error) {
      console.error("Error exporting leave report:", error);
      toast({
        title: "Erreur d'export",
        description:
          "Impossible d'exporter le rapport. Vérifiez la console pour plus de détails.",
        variant: "destructive",
      });
    }
  };

  // Handle view calendar navigation
  const handleViewCalendar = () => {
    setActiveTab("calendar");
  };

  // Handle view balance navigation
  const handleViewBalance = () => {
    setActiveTab("balance");
  };

  // Handle view request details
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowRequestDetail(true);
  };

  // Handle edit request (only for pending requests by current user)
  const handleEditRequest = (request) => {
    // Set the request to be edited and open the form
    setSelectedRequest(request);
    setShowRequestForm(true);
  };

  // Handle delete request (only for pending requests by current user)
  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) {
      return;
    }

    try {
      const response = await leaveAPI.deleteRequest(requestId);

      toast({
        title: "Succès",
        description: response.data.message || "Demande supprimée avec succès",
      });

      // Reload data to refresh the list
      await loadLeaveData();
    } catch (error) {
      console.error("Error deleting leave request:", error);

      const errorMessage =
        error.response?.data?.message || "Impossible de supprimer la demande";

      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Check if current user can approve a request (not their own request)
  const canApproveRequest = (request) => {
    if (!currentUser) return false;

    // User cannot approve their own request
    if (request.employe?.id === currentUser.id) return false;

    // Only managers, HR, and admin can approve
    return ["manager", "rh", "admin"].includes(currentUser.role);
  };

  // Check if current user can edit/delete a request
  const canModifyRequest = (request) => {
    if (!currentUser) return false;

    // Only the request owner can edit/delete, and only if status is pending
    return (
      request.employe?.id === currentUser.id && request.statut === "en_attente"
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      en_attente: { label: "En attente", variant: "secondary", icon: Clock },
      approuve: { label: "Approuvé", variant: "default", icon: CheckCircle },
      refuse: { label: "Refusé", variant: "destructive", icon: XCircle },
      annule: { label: "Annulé", variant: "outline", icon: CalendarX },
    };

    const config = statusConfig[status] || statusConfig.en_attente;
    const IconComponent = config.icon;

    return (
      <Badge
        variant={config.variant}
        className={cn(
          "flex items-center gap-1",
          status === "approuve" &&
          "bg-green-100 text-green-700 hover:bg-green-200",
          status === "en_attente" &&
          "bg-yellow-100 text-yellow-700 hover:bg-yellow-200",
          status === "refuse" && "bg-red-100 text-red-700 hover:bg-red-200"
        )}
      >
        <IconComponent className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Chargement des congés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Gestion des Congés
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Demandes, approbations et suivi des congés
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Exporter
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setSelectedRequest(null); // Clear any selected request for new request
                setShowRequestForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle demande
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards - Personal Stats for All Users */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <User className="h-5 w-5" />
          <h3 className="text-lg font-semibold">
            Mes statistiques personnelles
          </h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Mes demandes en attente
                  </p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {stats.pendingRequests}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Mes demandes approuvées
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.approvedRequests}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Mes jours utilisés
                  </p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stats.totalDaysUsed}
                  </p>
                </div>
                <CalendarDays className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Mes jours restants
                  </p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stats.remainingDays}
                  </p>
                </div>
                <Timer className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Stats Cards - Only for Admin/Manager/RH */}
      {currentUser &&
        ["admin", "rh", "manager"].includes(currentUser.role) &&
        stats.totalTeamRequests !== undefined && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <h3 className="text-lg font-semibold">
                Statistiques de l'équipe
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-amber-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Demandes équipe en attente
                      </p>
                      <p className="text-2xl font-bold text-amber-600">
                        {stats.pendingTeamRequests}
                      </p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Demandes équipe approuvées
                      </p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {stats.approvedTeamRequests}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-emerald-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-sky-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Jours équipe utilisés
                      </p>
                      <p className="text-2xl font-bold text-sky-600">
                        {stats.totalTeamDaysUsed}
                      </p>
                    </div>
                    <CalendarDays className="h-8 w-8 text-sky-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Équipe actuellement absente
                      </p>
                      <p className="text-2xl font-bold text-red-600">
                        {stats.currentTeamOnLeave}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

      {/* Main Content Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="border-b border-gray-200 dark:border-gray-700 px-6">
            <TabsList
              className={`grid w-full bg-transparent p-0 h-auto ${currentUser &&
                (currentUser.role === "admin" ||
                  currentUser.role === "rh" ||
                  currentUser.role === "manager")
                ? "grid-cols-6"
                : "grid-cols-3"
                }`}
            >
              {/* Mes demandes - available for ALL roles */}
              <TabsTrigger
                value="requests"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Mes demandes
              </TabsTrigger>

              {/* Admin/Manager/RH only tabs */}
              {currentUser &&
                (currentUser.role === "admin" ||
                  currentUser.role === "rh" ||
                  currentUser.role === "manager") && (
                  <>
                    <TabsTrigger
                      value="overview"
                      className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      Vue d'ensemble
                    </TabsTrigger>
                    <TabsTrigger
                      value="approvals"
                      className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      Approbations
                    </TabsTrigger>
                    <TabsTrigger
                      value="employee-credits"
                      className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                      Crédits employés
                    </TabsTrigger>
                  </>
                )}

              {/* Common tabs for all roles */}
              <TabsTrigger
                value="calendar"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Calendrier
              </TabsTrigger>
              <TabsTrigger
                value="balance"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Soldes
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Requests */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Demandes récentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {leaveRequests.slice(0, 5).map((request) => (
                        <div
                          key={request.id}
                          className="flex items-center justify-between p-4 rounded-lg border bg-card hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => handleViewRequest(request)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <p className="font-medium">
                                {request.employe.prenom} {request.employe.nom}
                              </p>
                              <div className="flex items-center gap-2">
                                <p className="text-sm text-muted-foreground">
                                  {request.type_conge.nom} •{" "}
                                  {request.nombre_jours} jour(s)
                                </p>
                                {request.justification && (
                                  <Badge variant="outline" className="text-xs">
                                    <FileText className="w-3 h-3 mr-1" />
                                    Justificatif
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Du{" "}
                                {new Date(
                                  request.date_debut.split("T")[0]
                                ).toLocaleDateString("fr-FR")}{" "}
                                au{" "}
                                {new Date(
                                  request.date_fin.split("T")[0]
                                ).toLocaleDateString("fr-FR")}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            {request.urgence && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Urgent
                              </Badge>
                            )}
                            {getStatusBadge(request.statut)}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRequest(request);
                              }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions & Balance */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="h-5 w-5" />
                      Actions rapides
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      className="w-full justify-start"
                      onClick={() => {
                        setSelectedRequest(null); // Clear any selected request for new request
                        setShowRequestForm(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Nouvelle demande
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setActiveTab("employee-credits")}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Crédits employés
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleViewCalendar}
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Voir le calendrier
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={handleViewBalance}
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Consulter mes soldes
                    </Button>
                  </CardContent>
                </Card>

                <LeaveBalance balance={leaveBalance} />
              </div>
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests" className="space-y-6">
            {/* Search and Filters */}
            <Card>
              <CardHeader>
                <CardTitle>Filtres</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  <Select
                    value={selectedStatus}
                    onValueChange={setSelectedStatus}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="approuve">Approuvé</SelectItem>
                      <SelectItem value="refuse">Refusé</SelectItem>
                      <SelectItem value="annule">Annulé</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Type de congé">
                        {selectedType === "all"
                          ? "Tous les types"
                          : leaveTypes.find((t) => t.id === selectedType)
                            ?.nom || `Type ${selectedType}`}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {leaveTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.nom}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                    <SelectTrigger>
                      <SelectValue placeholder="Employé">
                        {selectedEmployee === "all"
                          ? "Tous les employés"
                          : employees.find((e) => e.id === selectedEmployee)
                            ? `${employees.find((e) => e.id === selectedEmployee).prenom} ${employees.find((e) => e.id === selectedEmployee).nom}`
                            : `Employé ${selectedEmployee}`}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les employés</SelectItem>
                      {employees.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          {employee.prenom} {employee.nom} ({employee.numero_employe})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button variant="outline">
                    <Filter className="h-4 w-4 mr-2" />
                    Plus de filtres
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Requests List */}
            <Card>
              <CardHeader>
                <CardTitle>Liste des demandes ({requestCount})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredRequests.map((request) => (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className="w-4 h-4 rounded"
                          style={{
                            backgroundColor: request.type_conge.couleur,
                          }}
                        ></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {request.type_conge.nom}
                            </p>
                            {request.urgence && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Urgent
                              </Badge>
                            )}
                            {request.justification && (
                              <Badge variant="outline" className="text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                Justificatif
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Du{" "}
                            {new Date(
                              request.date_debut.split("T")[0]
                            ).toLocaleDateString("fr-FR")}{" "}
                            au{" "}
                            {new Date(
                              request.date_fin.split("T")[0]
                            ).toLocaleDateString("fr-FR")}{" "}
                            • {request.nombre_jours} jour(s)
                          </p>
                          {request.motif && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Motif: {request.motif}
                            </p>
                          )}
                          {request.justification && (
                            <div className="flex items-center gap-2 text-xs text-blue-600 mt-1">
                              <FileText className="w-3 h-3" />
                              <span>{request.justification.fileName}</span>
                              <button
                                onClick={async () => {
                                  try {
                                    const response =
                                      await leaveAPI.downloadJustification(
                                        request.id
                                      );

                                    const blob = new Blob([response.data]);
                                    const url =
                                      window.URL.createObjectURL(blob);
                                    const filename =
                                      request.justification?.fileName ||
                                      `justification-${request.id}.pdf`;

                                    const link = document.createElement("a");
                                    link.href = url;
                                    link.download = filename;
                                    document.body.appendChild(link);
                                    link.click();

                                    document.body.removeChild(link);
                                    window.URL.revokeObjectURL(url);

                                    toast({
                                      title: "Succès",
                                      description:
                                        "Fichier téléchargé avec succès",
                                    });
                                  } catch (error) {
                                    console.error(
                                      "Error downloading justification:",
                                      error
                                    );
                                    toast({
                                      title: "Erreur",
                                      description:
                                        "Erreur lors du téléchargement du fichier",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition-colors"
                                title="Télécharger le justificatif"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                          <p className="text-xs text-muted-foreground">
                            Demandé le{" "}
                            {new Date(request.date_demande).toLocaleDateString(
                              "fr-FR"
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {getStatusBadge(request.statut)}
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewRequest(request)}
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canModifyRequest(request) && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditRequest(request)}
                                title="Modifier la demande"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRequest(request.id)}
                                title="Supprimer la demande"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Approvals Tab */}
          <TabsContent value="approvals" className="space-y-6">
            <LeaveApproval
              requests={leaveRequests.filter(
                (r) => r.statut === "en_attente" && canApproveRequest(r)
              )}
              onApprove={handleApproveRequest}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* Employee Credits Tab */}
          <TabsContent value="employee-credits" className="space-y-6">
            <EmployeeLeaveCredits
              employees={employees}
              currentUserRole={currentUser?.role}
            />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-6">
            <LeaveCalendar
              leaveRequests={leaveRequests}
              leaveTypes={leaveTypes}
            />
          </TabsContent>

          {/* Balance Tab */}
          <TabsContent value="balance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeaveBalance balance={leaveBalance} detailed />

              <Card>
                <CardHeader>
                  <CardTitle>Historique des congés</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {leaveRequests
                      .filter(
                        (r) =>
                          r.statut === "approuve" &&
                          r.employe.id === currentUser.id
                      )
                      .map((request) => (
                        <div
                          key={request.id}
                          className="flex justify-between items-center p-3 rounded border"
                        >
                          <div>
                            <p className="font-medium">
                              {request.type_conge.nom}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(
                                request.date_debut.split("T")[0]
                              ).toLocaleDateString("fr-FR")}{" "}
                              -{" "}
                              {new Date(
                                request.date_fin.split("T")[0]
                              ).toLocaleDateString("fr-FR")}
                            </p>
                          </div>
                          <Badge variant="outline">
                            {request.nombre_jours} jour(s)
                          </Badge>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      {showRequestForm && (
        <LeaveRequestForm
          isOpen={showRequestForm}
          onClose={() => {
            setShowRequestForm(false);
            setSelectedRequest(null); // Clear selection on close
          }}
          leaveTypes={leaveTypes}
          onSubmit={handleCreateLeaveRequest}
          editRequest={selectedRequest} // Pass selected request for editing
          employees={employees}
          currentUserRole={currentUser?.role || 'employe'}
        />
      )}

      {/* Request Detail Modal */}
      {showRequestDetail && selectedRequest && (
        <Dialog open={showRequestDetail} onOpenChange={setShowRequestDetail}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Détails de la demande de congé
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Request Header */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <h3 className="font-semibold text-lg">
                    {selectedRequest.employe.prenom}{" "}
                    {selectedRequest.employe.nom}
                  </h3>
                  <p className="text-muted-foreground">
                    {selectedRequest.employe.departement?.nom}
                  </p>
                </div>
                <div className="text-right">
                  {getStatusBadge(selectedRequest.statut)}
                  {selectedRequest.urgence && (
                    <Badge variant="destructive" className="ml-2">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Urgent
                    </Badge>
                  )}
                </div>
              </div>

              {/* Request Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Type de congé
                    </label>
                    <div className="flex items-center gap-2 mt-1">
                      <div
                        className="w-3 h-3 rounded"
                        style={{
                          backgroundColor: selectedRequest.type_conge.couleur,
                        }}
                      ></div>
                      <span>{selectedRequest.type_conge.nom}</span>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Date de début
                    </label>
                    <p className="mt-1">
                      {new Date(
                        selectedRequest.date_debut.split("T")[0]
                      ).toLocaleDateString("fr-FR", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Date de fin
                    </label>
                    <p className="mt-1">
                      {new Date(
                        selectedRequest.date_fin.split("T")[0]
                      ).toLocaleDateString("fr-FR", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Nombre de jours
                    </label>
                    <p className="mt-1 text-2xl font-bold text-blue-600">
                      {selectedRequest.nombre_jours}
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Date de demande
                    </label>
                    <p className="mt-1">
                      {new Date(
                        selectedRequest.date_demande
                      ).toLocaleDateString("fr-FR")}
                    </p>
                  </div>

                  {selectedRequest.date_approbation && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Date d'approbation
                      </label>
                      <p className="mt-1">
                        {new Date(
                          selectedRequest.date_approbation
                        ).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Motif */}
              {selectedRequest.motif && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Motif
                  </label>
                  <p className="mt-1 p-3 bg-muted rounded">
                    {selectedRequest.motif}
                  </p>
                </div>
              )}

              {/* Justification File */}
              {selectedRequest.justification && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Justificatif
                  </label>
                  <Card className="mt-1 bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">
                              {selectedRequest.justification.fileName}
                            </p>
                            <p className="text-xs text-blue-600">
                              {(
                                selectedRequest.justification.fileSize / 1024
                              ).toFixed(1)}{" "}
                              KB
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response =
                                await leaveAPI.downloadJustification(
                                  selectedRequest.id
                                );

                              // Create blob from response
                              const blob = new Blob([response.data]);
                              const url = window.URL.createObjectURL(blob);

                              // Get filename from justification data or use default
                              const filename =
                                selectedRequest.justification?.fileName ||
                                `justification-${selectedRequest.id}.pdf`;

                              // Create download link
                              const link = document.createElement("a");
                              link.href = url;
                              link.download = filename;
                              document.body.appendChild(link);
                              link.click();

                              // Cleanup
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);

                              toast({
                                title: "Succès",
                                description: "Fichier téléchargé avec succès",
                              });
                            } catch (error) {
                              console.error(
                                "Error downloading justification:",
                                error
                              );
                              toast({
                                title: "Erreur",
                                description:
                                  "Erreur lors du téléchargement du fichier",
                                variant: "destructive",
                              });
                            }
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Télécharger
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Approval comment */}
              {selectedRequest.commentaire_approbation && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Commentaire d'approbation
                  </label>
                  <p className="mt-1 p-3 bg-muted rounded">
                    {selectedRequest.commentaire_approbation}
                  </p>
                </div>
              )}

              {/* Action buttons for admins/managers */}
              {canApproveRequest(selectedRequest) &&
                selectedRequest.statut === "en_attente" && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      onClick={() => {
                        setShowRequestDetail(false);
                        setActiveTab("approvals");
                      }}
                      className="flex-1"
                    >
                      Traiter la demande
                    </Button>
                  </div>
                )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
